import { IconBug, IconChevronDown } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, linkOptions, Outlet } from "@tanstack/react-router";
import z from "zod";
import { useDialogManager } from "@/components/dialog-manager";
import { SiteHeader } from "@/components/site-header";
import { TabBar, type TabBarTabs } from "@/components/tab-bar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs } from "@/components/ui/tabs";
import { orpc } from "@/orpc/client";

export const Route = createFileRoute("/_app/users/$userId")({
	component: Page,
	params: z.object({
		userId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (!preload) {
			return;
		}

		context.queryClient.prefetchQuery(
			orpc.users.show.queryOptions({
				input: {
					userId: params.userId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { openDialog } = useDialogManager();

	const { data } = useSuspenseQuery(
		orpc.users.show.queryOptions({
			input: {
				userId: params.userId,
			},
		}),
	);

	const tabs: TabBarTabs = linkOptions([
		{
			key: "details",
			to: "/users/$userId",
			params: { userId: data.ID },
			activeOptions: { exact: true },
			label: "Details",
		},
		{
			key: "channels",
			to: "/users/$userId/channels",
			params: { userId: data.ID },
			label: "Podcasts",
			count: data.channelsCount,
		},
		{
			key: "reset-password",
			to: "/users/$userId/reset-password",
			params: { userId: data.ID },
			label: "Reset Password",
		},
	]);

	return (
		<>
			<SiteHeader
				breadcrumbs={[
					{
						key: "users",
						label: "Users",
						linkProps: { to: "/users" },
					},
					{
						key: "user",
						label: data.UserName ?? "User Details",
					},
				]}
			/>
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-2">
					<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
						<div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
							<div className="flex w-full flex-col justify-between gap-2 md:flex-row md:gap-4 lg:items-end">
								<div className="flex gap-3">
									<Avatar className="size-16">
										<AvatarFallback>{data.UserName.slice(0, 2).toUpperCase()}</AvatarFallback>
									</Avatar>
									<div>
										<h1 className="text-lg leading-tight font-semibold">{data.UserName}</h1>
										<p className="text-muted-foreground text-sm">{data.EmailAddress}</p>
										<p className="text-muted-foreground text-sm">User ID: #{data.ID}</p>
									</div>
								</div>
								<div className="flex items-center justify-end gap-2">
									<DropdownMenu>
										<DropdownMenuTrigger
											render={
												<Button variant="outline" aria-label="More Options">
													Actions
													<IconChevronDown />
												</Button>
											}
										/>
										<DropdownMenuContent align="end" className="min-w-40">
											<DropdownMenuGroup>
												<DropdownMenuItem
													onClick={() =>
														openDialog({
															type: "debug",
															payload: { title: `User #${data.ID}`, data },
														})
													}
												>
													<IconBug />
													Debug
												</DropdownMenuItem>
											</DropdownMenuGroup>
										</DropdownMenuContent>
									</DropdownMenu>
								</div>
							</div>
						</div>
						<Tabs>
							<div className="px-4 lg:px-6">
								<TabBar tabs={tabs} />
							</div>
							<Outlet />
						</Tabs>
					</div>
				</div>
			</div>
		</>
	);
}
